import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Control, useController, useWatch } from 'react-hook-form'
import { Input, FormGroup } from 'reactstrap'
import { LoadIndicator } from 'devextreme-react'
import useAsyncService from '../../../hooks/useAsyncService'
import { getAllAnalyzers } from '../../../services/endpoints'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { Analyzer } from '../../Analyzers/types'
import { AnalyzerFilterType, ResultsSearchValues } from '../types'

const DEFAULT_OPTION = { label: 'All analyzers', value: '' }
const SELECT_TYPE_OPTION = {
  label: 'Select an analyzer type',
  value: ''
}

interface AnalyzerPickerProps {
  control: Control<ResultsSearchValues>
  name: keyof ResultsSearchValues
}

const AnalyzerPicker: React.FC<AnalyzerPickerProps> = ({ control, name }) => {
  const analyzerType = useWatch({
    control,
    name: 'analyzerType'
  }) as AnalyzerFilterType | null

  const fetchAnalyzers = useCallback(
    ({ type }: { type: AnalyzerFilterType }) => getAllAnalyzers({ type }),
    []
  )

  const { serviceValue, serviceLoading, serviceError, setServiceArgs } =
    useAsyncService<{ type: AnalyzerFilterType }, Analyzer[]>({
      serviceCall: fetchAnalyzers,
      delay: 0,
      initialArgs: null
    })
  const previousAnalyzerType = useRef<AnalyzerFilterType | null>(null)

  const {
    field: { value, onChange },
    fieldState: { invalid }
  } = useController<ResultsSearchValues>({
    name: name as keyof ResultsSearchValues,
    control
  })

  useEffect(() => {
    if (previousAnalyzerType.current === analyzerType) {
      return
    }

    previousAnalyzerType.current = analyzerType

    // Reset analyzer selection whenever the type changes so stale values do not persist
    onChange(null)

    if (!analyzerType) {
      setServiceArgs(null)
      return
    }

    setServiceArgs({ type: analyzerType })
  }, [analyzerType, setServiceArgs, onChange])

  useEffect(() => {
    if (!serviceValue || !value) {
      return
    }

    const isExistingAnalyzer = serviceValue.some(
      (analyzer) => analyzer.id === value
    )

    if (!isExistingAnalyzer) {
      onChange(null)
    }
  }, [serviceValue, value, onChange])

  const options = useMemo(() => {
    if (!analyzerType) {
      return [SELECT_TYPE_OPTION]
    }

    if (!serviceValue || serviceValue.length === 0) {
      return [DEFAULT_OPTION]
    }

    const analyzerOptions = serviceValue
      .map((analyzer) => ({
        label: analyzer.name,
        value: analyzer.id
      }))
      .sort((a, b) => a.label.localeCompare(b.label))

    return [DEFAULT_OPTION, ...analyzerOptions]
  }, [serviceValue, analyzerType])

  if (!analyzerType) {
    return (
      <FormGroup>
        <Input
          id="resultsAnalyzerPicker"
          type="select"
          value={typeof value === 'string' ? value : ''}
          disabled
          invalid={invalid}
          onChange={() => undefined}
        >
          <option value="">{SELECT_TYPE_OPTION.label}</option>
        </Input>
      </FormGroup>
    )
  }

  if (serviceLoading) {
    return <LoadIndicator />
  }

  if (serviceError) {
    return <ICSErrorCard code="error" message={serviceError} fields={[]} />
  }

  return (
    <FormGroup>
      <Input
        id="resultsAnalyzerPicker"
        type="select"
        value={typeof value === 'string' ? value : ''}
        invalid={invalid}
        onChange={(event) => {
          const nextValue = event.target.value
          onChange(nextValue ? nextValue : null)
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    </FormGroup>
  )
}

export default AnalyzerPicker
